import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, MapPin, Car, DollarSign, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const travelFeeAPI = {
  calculate: (destinationAddress) => 
    axios.post(`${API}/travel-fee/calculate`, { destination_address: destinationAddress }).then(res => res.data),
};

const TravelFeeSection = ({
  isExpanded,
  onToggle,
  location,
  travelFee,
  travelDistanceKm,
  travelChargeableKm,
  onTravelFeeCalculated
}) => {
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState(null);
  const [calculationResult, setCalculationResult] = useState(null);

  const handleCalculate = async () => {
    if (!location || location.trim() === '') {
      setError('Please enter an event location first');
      return;
    }

    setCalculating(true);
    setError(null);
    
    try {
      const result = await travelFeeAPI.calculate(location);
      setCalculationResult(result);
      
      // Pass the calculated values back to parent
      onTravelFeeCalculated({
        travelFee: result.travel_fee,
        travelDistanceKm: result.distance_km,
        travelChargeableKm: result.chargeable_km
      });
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to calculate travel fee. Please check the address.';
      setError(errorMsg);
      console.error('Travel fee calculation error:', err);
    } finally {
      setCalculating(false);
    }
  };

  const handleClearFee = () => {
    setCalculationResult(null);
    onTravelFeeCalculated({
      travelFee: 0,
      travelDistanceKm: null,
      travelChargeableKm: null
    });
  };

  return (
    <div className="border-b pb-4">
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center justify-between w-full text-left font-semibold text-[#1e3a5f] mb-3"
        data-testid="travel-fee-section-toggle"
      >
        <span className="flex items-center gap-2">
          <Car size={18} />
          Travel Fee Calculator
          {travelFee > 0 && (
            <span className="text-sm font-normal text-amber-600 ml-2">
              (${travelFee.toFixed(2)} added)
            </span>
          )}
        </span>
        <span className="text-sm text-gray-500">{isExpanded ? '−' : '+'}</span>
      </button>

      {isExpanded && (
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-4 rounded-lg space-y-4">
          {/* Info Banner */}
          <div className="bg-white/80 border border-amber-200 rounded-lg p-3 text-sm">
            <p className="font-medium text-amber-800 mb-1">Cocktail Coach Travel Policy</p>
            <p className="text-gray-600">
              First <strong>50 km</strong> from Blenheim, ON is included. 
              Distance beyond 50 km is charged at <strong>$1/km</strong>.
            </p>
          </div>

          {/* Current Location Display */}
          <div>
            <Label className="text-sm font-medium text-gray-700 mb-1 block">Event Location</Label>
            <div className="flex items-center gap-2 p-2 bg-white rounded border">
              <MapPin size={16} className="text-gray-400" />
              <span className={`text-sm ${location ? 'text-gray-800' : 'text-gray-400'}`}>
                {location || 'Enter event location in Event Details section above'}
              </span>
            </div>
          </div>

          {/* Calculate Button */}
          <div className="flex gap-2">
            <Button
              type="button"
              onClick={handleCalculate}
              disabled={calculating || !location}
              className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="calculate-travel-fee-btn"
            >
              {calculating ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Calculating...
                </>
              ) : (
                <>
                  <Car size={16} className="mr-2" />
                  Calculate Travel Fee
                </>
              )}
            </Button>
            {travelFee > 0 && (
              <Button
                type="button"
                variant="outline"
                onClick={handleClearFee}
                className="text-gray-600"
                data-testid="clear-travel-fee-btn"
              >
                Clear
              </Button>
            )}
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Results Display */}
          {calculationResult && (
            <div className="bg-white border border-amber-200 rounded-lg overflow-hidden" data-testid="travel-fee-result">
              <div className="bg-amber-100 px-4 py-2 border-b border-amber-200">
                <p className="font-semibold text-amber-900">Travel Fee Calculation</p>
              </div>
              <div className="p-4 space-y-3">
                {/* Route Info */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500 text-xs uppercase tracking-wide">From</p>
                    <p className="font-medium">Blenheim, ON</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs uppercase tracking-wide">To</p>
                    <p className="font-medium truncate" title={calculationResult.destination}>
                      {calculationResult.destination}
                    </p>
                  </div>
                </div>

                {/* Distance & Duration */}
                <div className="grid grid-cols-2 gap-4 pt-3 border-t">
                  <div className="bg-gray-50 rounded p-3 text-center">
                    <p className="text-2xl font-bold text-gray-800">{calculationResult.distance_text}</p>
                    <p className="text-xs text-gray-500">Total Distance</p>
                  </div>
                  <div className="bg-gray-50 rounded p-3 text-center">
                    <p className="text-2xl font-bold text-gray-800">{calculationResult.duration_text}</p>
                    <p className="text-xs text-gray-500">Drive Time</p>
                  </div>
                </div>

                {/* Fee Breakdown */}
                <div className="pt-3 border-t space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Distance</span>
                    <span>{calculationResult.distance_km} km</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Included (Free)</span>
                    <span className="text-green-600">-{calculationResult.included_km} km</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Chargeable Distance</span>
                    <span>{calculationResult.chargeable_km} km</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Rate</span>
                    <span>${calculationResult.cost_per_km.toFixed(2)}/km</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t font-bold text-lg">
                    <span className="flex items-center gap-1">
                      <DollarSign size={18} />
                      Travel Fee
                    </span>
                    <span className={calculationResult.travel_fee > 0 ? 'text-amber-600' : 'text-green-600'}>
                      ${calculationResult.travel_fee.toFixed(2)}
                    </span>
                  </div>
                </div>

                {calculationResult.travel_fee === 0 && (
                  <div className="bg-green-50 border border-green-200 rounded p-2 text-center">
                    <p className="text-green-700 text-sm font-medium">
                      🎉 No travel fee - within 50km radius!
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Already calculated indicator */}
          {!calculationResult && travelFee > 0 && (
            <div className="bg-amber-100 border border-amber-200 rounded-lg p-3">
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium text-amber-900">Travel Fee Applied</p>
                  <p className="text-sm text-amber-700">
                    {travelDistanceKm} km total • {travelChargeableKm} km chargeable
                  </p>
                </div>
                <span className="text-xl font-bold text-amber-800">
                  ${travelFee.toFixed(2)}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TravelFeeSection;
